
import React from 'react';
import ReactDOM from 'react-dom';
import '../../../../styles/moreInfoRenderer.scss';

// Shared components and services
import { OmniIconComponent, OmniTooltipNextComponent, MediaOpsService } from '../../../../shared';

class MoreInfoRenderer extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            position: { top: 0, left: 0 },
            isPositionedAbove: false
        };
        this.buttonRef = React.createRef();
        this.menuRef = React.createRef();
        this.menuContainer = document.createElement('div');
        this.menuContainer.className = 'dropdown-portal-container';
        this.isMenuContainerAppended = false;
    }

    componentDidMount() {
        if (!this.isMenuContainerAppended) {
            document.body.appendChild(this.menuContainer);
            this.isMenuContainerAppended = true;
        }
        document.addEventListener('mousedown', this.handleOutsideClick, true);
        document.addEventListener('scroll', this.handleOutsideClick, true);
    }

    componentDidUpdate(prevProps) {
        if (this.props.isOpen && !prevProps.isOpen) {
            requestAnimationFrame(() => {
                if (this.menuRef.current?.offsetHeight > 0) {
                    this.updatePositionWithActualHeight();
                }
            });
        }
    }

    componentWillUnmount() {
        if (this.isMenuContainerAppended) {
            document.body.removeChild(this.menuContainer);
            this.isMenuContainerAppended = false;
        }
        document.removeEventListener('mousedown', this.handleOutsideClick, true);
        document.removeEventListener('scroll', this.handleOutsideClick, true);
    }

    handleOutsideClick = (e) => {
        // Only handle clicks if this specific instance's menu is open
        if (!this.props.isOpen) {
            return;
        }
        
        if (
            this.buttonRef.current &&
            !this.buttonRef.current.contains(e.target) &&
            this.menuContainer &&
            !this.menuContainer.contains(e.target)
        ) {
            this.props.onToggle(null);
        }
    };

    getMenuHeight = () => {
        if (this.menuRef.current) {
            return this.menuRef.current.offsetHeight;
        }
        
        // Calculate estimated height based on content
        const baseHeight = 80; // Card padding, borders, margins
        const headerHeight = 60; // Header with title and close button
        const infoGridHeight = 150; // 3 info items (50px each)
        
        let fullWidthItemsHeight = 0;
        
        const partnersContent = MediaOpsService.getPartnerString(this.props?.campaign);
        if (partnersContent?.trim()) {
            fullWidthItemsHeight += 50;
        }
        
        const filename = this.props?.campaign?.filename;
        if (filename?.trim()) {
            fullWidthItemsHeight += 50;
        }
        
        return baseHeight + headerHeight + infoGridHeight + fullWidthItemsHeight;
    };

    updatePosition = () => {
        if (!this.buttonRef.current) return;

        const rect = this.buttonRef.current.getBoundingClientRect();
        const menuHeight = this.getMenuHeight();
        const effectiveMenuHeight = menuHeight > 0 ? menuHeight : 350; // Fallback if not rendered
        
        const viewportHeight = window.innerHeight;
        const spaceAbove = rect.top;
        
        // Position above if in bottom 40% of viewport and has enough space
        const isNearBottom = rect.bottom > (viewportHeight * 0.6);
        const hasEnoughSpaceAbove = spaceAbove > 200;
        const shouldPositionAbove = isNearBottom && hasEnoughSpaceAbove;

        const newPosition = {
            top: shouldPositionAbove 
                ? rect.top + window.scrollY - effectiveMenuHeight + 50
                : rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX + 120,
        };

        this.setState({
            position: newPosition,
            isPositionedAbove: shouldPositionAbove
        });
    };

    updatePositionWithActualHeight = () => {
        if (!this.buttonRef.current || !this.menuRef.current) return;

        const rect = this.buttonRef.current.getBoundingClientRect();
        const actualMenuHeight = this.menuRef.current.offsetHeight;
        const viewportHeight = window.innerHeight;
        const spaceAbove = rect.top;
        
        // Use actual height for precise space calculation
        const isNearBottom = rect.bottom > (viewportHeight * 0.6);
        const hasEnoughSpaceAbove = spaceAbove > actualMenuHeight + 50;
        const shouldPositionAbove = isNearBottom && hasEnoughSpaceAbove;

        const newPosition = {
            top: shouldPositionAbove 
                ? rect.top + window.scrollY - actualMenuHeight - 10                
                : rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX + 120,
        };

        this.setState({
            position: newPosition,
            isPositionedAbove: shouldPositionAbove
        });
    };

    toggleMenu = (e) => {
        e.stopPropagation();
        this.updatePosition();
        this.props.onToggle(this.props.campaign?.id);
    };

    renderMenu() {
        const { position, isPositionedAbove } = this.state;
        const { top, left } = position;
        if (!this.props.isOpen) return null;

        const menuStyle = {
            top,
            left,
            position: 'absolute',
            zIndex: 9999
        };

        return ReactDOM.createPortal(
            <div className="moreinfo-dropdown-portal-menu" style={menuStyle}>
                <div className="dropdown-content-actions" ref={this.menuRef}>
                    <div className="more-info-card">
                        <div className="card-header" style={{ alignItems: 'center' }}>
                            <span>More info</span>
                            <button 
                                aria-haspopup="true" 
                                className="icon medium" 
                                aria-label="Close" 
                                onClick={this.toggleMenu}
                            >
                                <OmniIconComponent icon-id="omni:interactive:close" />
                            </button>
                        </div>

                        <div className="info-grid">
                            <div className="info-item">
                                <div className="label">Source</div>
                                <div className="value">{this.props?.campaign?.source}</div>
                            </div>
                            <div className="info-item">
                                <div className="label">Client</div>
                                <div className="value">{this.props?.campaign?.clientName}</div>
                            </div>
                            <div className="info-item">
                                <div className="label">Submitted Version</div>
                                <div className="value">{MediaOpsService.getSubmittedVersion(this.props?.campaign?.filerevision)}</div>
                            </div>
                        </div>

                        <div className="info-item full-width">
                            <div className="label" style={{ marginLeft: 0 }}>Partners</div>
                            <div className="value">{MediaOpsService.getPartnerString(this.props?.campaign)}</div>
                        </div>

                        <div className="info-item full-width">
                            <div className="label" style={{ marginLeft: 0 }}>File name</div>
                            <div className="value">{this.props?.campaign?.filename}</div>
                        </div>
                    </div>


                </div>
            </div>,
            this.menuContainer
        );
    }

    render() {
        return (
                <>
                <OmniTooltipNextComponent>
                    <button 
                        slot="invoker" 
                        aria-haspopup="true" 
                        className="icon medium" 
                        ref={this.buttonRef} 
                        onClick={this.toggleMenu}
                    >
                        <OmniIconComponent icon-id="omni:informative:info" />
                    </button>
                    <div slot="content">More info</div>
                </OmniTooltipNextComponent>

                {this.renderMenu()}
            </>
        );
    }
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(MoreInfoRenderer, (prevProps, nextProps) => {
    // Return true when props are equal (to skip re-render)
    // Return false when props differ (to trigger re-render)
    
    // Check campaign properties that are displayed in the UI
    const campaignPropsAreEqual =
        prevProps.campaign?.id === nextProps.campaign?.id &&
        prevProps.campaign?.source === nextProps.campaign?.source &&
        prevProps.campaign?.clientName === nextProps.campaign?.clientName &&
        prevProps.campaign?.filerevision === nextProps.campaign?.filerevision &&
        prevProps.campaign?.filename === nextProps.campaign?.filename &&
        MediaOpsService.getPartnerString(prevProps.campaign) === MediaOpsService.getPartnerString(nextProps.campaign);

    return (
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.onToggle === nextProps.onToggle &&
        campaignPropsAreEqual
    );
});
